import { Box, Button, Container, styled } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MultiStepModal, { ModalStepT } from 'ui/Modals/MultiStepModal';
import {
    FreeHypothesisEditMutationParams,
    FreeHypothesis,
    OperationType,
    ValueType,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/helpers/types';
import { getTodayDateString } from 'components/inputs/dateHelpers';
import {
    HrSimulation,
    SimulationDateString,
} from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { PayComponent } from 'routes/HumanResourcesModule/PayState/helpers/constants';
import ApplyConditionsStep from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/components/ApplyConditionsStep';
import IfseInformationStep from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/components/IfseInformationStep';
import FreeHypothesesListView from './components/informationDisplay/FreeHypothesisListView';
import { useFreeHypothesisCRUDMutations } from './helpers/hooks/freeHypothesis';
import { useTranslation } from 'react-i18next';

type FreeHypothesisCreationModalProps = {
    hrSimulation: HrSimulation;
    freeHypotheses: FreeHypothesis[];
    freeHypothesisTotals: Record<string, number>;
    setFreeHypothesisUpdatePayload: (
        payload: FreeHypothesisEditMutationParams,
    ) => void;
    onClose: () => void;
    show: boolean;
    hasChange: () => void;
};
const StyBox = styled(Box)`
    height: '10rem';
`;
export default function FreeHypothesisEditModal({
    hrSimulation,
    freeHypotheses,
    freeHypothesisTotals,
    onClose,
    show,
    setFreeHypothesisUpdatePayload,
    hasChange,
}: FreeHypothesisCreationModalProps): React.ReactElement {
    const { t } = useTranslation();
    const [selectedHypothesis, setSelectedHypothesis] =
        useState<FreeHypothesis | null>(null);

    const { deleteFreeHypothesis } = useFreeHypothesisCRUDMutations({
        hrSimulationId: hrSimulation.id,
        hypothesisId: selectedHypothesis?.id,
    });

    const [category, setCategory] = useState<string>('');
    const [cadreEmploi, setCadreEmploi] = useState<string>('');
    const [filiere, setFiliere] = useState<string>('');
    const [jobs, setJobs] = useState<string[]>([]);
    const [agentsNumber, setAgentsNumber] = useState<number>(0);
    const [minValue, setMinValue] = useState<number | undefined>(0);
    const [maxValue, setMaxValue] = useState<number | undefined>(0);
    const [newValue, setNewValue] = useState<number | undefined>(0);
    const [operationType, setOperationType] = useState<OperationType>(
        OperationType.FIX,
    );

    const [valueType, setValueType] = useState<ValueType>(ValueType.AMOUNT);
    const [applicationDate, setApplicationDate] =
        useState<SimulationDateString>(
            getTodayDateString() as SimulationDateString,
        );
    const isValuesValid = Boolean((maxValue ?? 0) > (minValue ?? 0));
    const isEditValid = Boolean(newValue && agentsNumber > 0);

    useEffect(() => {
        // if only 1 hypothesis exists,
        // it is defined as the selected hypothesis and the first step is skipped
        if (freeHypotheses.length === 1) {
            setSelectedHypothesis(freeHypotheses[0]);
        }
    }, [freeHypotheses]);

    useEffect(() => {
        if (selectedHypothesis && selectedHypothesis.filters) {
            setOperationType(selectedHypothesis?.operation);
            setValueType(selectedHypothesis?.valueType);
            setNewValue(selectedHypothesis?.value ?? 0);
            setCategory(selectedHypothesis?.filters.category ?? '');
            setCadreEmploi(selectedHypothesis?.filters?.cadreEmploi ?? '');
            setFiliere(selectedHypothesis?.filters.filiere ?? '');
            setJobs(selectedHypothesis?.filters.jobLabel ?? []);
            setMaxValue(selectedHypothesis?.filters.lowerThan ?? 0);
            setMinValue(selectedHypothesis?.filters.greaterThan ?? 0);
            setApplicationDate(
                (selectedHypothesis?.filters.date as SimulationDateString) ??
                    applicationDate,
            );
        }
    }, [selectedHypothesis]);

    const onDelete = useCallback(() => {
        deleteFreeHypothesis();
        hasChange();
        onClose();
    }, []);

    const onValidate = useCallback(() => {
        if (selectedHypothesis) {
            const updatedHypothesis: FreeHypothesisEditMutationParams = {
                id: selectedHypothesis?.id,
                operation: operationType,
                valueType,
                newValue: newValue ?? 0,
                filters: {
                    category,
                    cadreEmploi,
                    filiere,
                    jobLabel: jobs,
                    greaterThan: minValue,
                    lowerThan: maxValue,
                    date: applicationDate,
                },
                agentsNumber,
            };
            setFreeHypothesisUpdatePayload(updatedHypothesis);
            hasChange();
            onClose();
        }
    }, [
        selectedHypothesis,
        operationType,
        applicationDate,
        valueType,
        newValue,
        category,
        cadreEmploi,
        filiere,
        jobs,
        minValue,
        maxValue,
        agentsNumber,
    ]);

    const firstStep = {
        title: t(
            'humanResources.simulation.freeHypothesis.hypothesisType_other',
            {
                type: 'IFSE',
            },
        ),
        confirmAction: {
            enabled: selectedHypothesis !== null,
        },
        node: (
            <Container
                sx={{
                    paddingX: '1rem 16rem',
                    display: 'flex',
                    alignItems: 'center ',
                    height: '100% !important',
                    justifyContent: 'center',
                    width: '100%',
                    maxWidth: 'none',
                }}
            >
                <FreeHypothesesListView
                    freeHypothesisData={freeHypotheses}
                    freeHypothesisTotals={freeHypothesisTotals}
                    selectedHypothesis={selectedHypothesis}
                    setSelectedHypothesis={setSelectedHypothesis}
                />
            </Container>
        ),
    };

    const secondStep = {
        title: t(
            'humanResources.simulation.freeHypothesis.hypothesisType_one',
            {
                type: PayComponent.IFSE_AND_MONTHLY_PRIMES,
            },
        ),
        confirmAction: {
            enabled: isValuesValid,
        },
        leftActionGroup: (
            <Button variant="contained" color="error" onClick={onDelete}>
                {t('common.delete')}
            </Button>
        ),
        node: (
            <IfseInformationStep
                editMode
                hrSimulationId={hrSimulation.id}
                category={category}
                setCategory={setCategory}
                cadreEmploi={cadreEmploi}
                setCadreEmploi={setCadreEmploi}
                filiere={filiere}
                setFiliere={setFiliere}
                jobs={jobs}
                setJobs={setJobs}
                minValue={minValue ?? 0}
                setMinValue={setMinValue}
                maxValue={maxValue ?? 0}
                setMaxValue={setMaxValue}
            />
        ),
    };

    const thirdStep = {
        title: t(
            'humanResources.simulation.freeHypothesis.hypothesisType_one',
            {
                type: PayComponent.IFSE_AND_MONTHLY_PRIMES,
            },
        ),
        node: useMemo(
            () => (
                <StyBox>
                    <ApplyConditionsStep
                        hypothesisId={selectedHypothesis?.id}
                        agentsNumber={agentsNumber}
                        setAgentsNumber={setAgentsNumber}
                        hrSimulation={hrSimulation}
                        operationType={operationType}
                        setOperationType={setOperationType}
                        valueType={valueType}
                        setValueType={setValueType}
                        category={category}
                        cadreEmploi={cadreEmploi}
                        filiere={filiere}
                        minValue={minValue ?? 0}
                        maxValue={maxValue ?? 0}
                        applicationDate={applicationDate}
                        setApplicationDate={setApplicationDate}
                        setNewValue={setNewValue}
                        job={jobs}
                        newValue={newValue}
                    />
                </StyBox>
            ),
            [
                agentsNumber,
                applicationDate,
                cadreEmploi,
                category,
                filiere,
                hrSimulation,
                jobs,
                maxValue,
                minValue,
                newValue,
                operationType,
                valueType,
            ],
        ),
        confirmAction: {
            label: t('common.modify'),
            enabled: isEditValid,
            onClick: onValidate,
        },
    };

    // if there is only one hypothesis to edit, the dialog directly display the edit hypothesis step
    const steps: ModalStepT[] = [secondStep, thirdStep];
    if (freeHypotheses.length > 1) {
        steps.unshift(firstStep);
    }

    return (
        <MultiStepModal
            isOpen={show}
            onClose={onClose}
            isSmallHeight
            maxWidth="md"
            PaperProps={{
                sx: {
                    width: '70rem',
                },
            }}
            steps={steps}
        />
    );
}
